/**
 * The website → demo hand-over, as a state machine.
 *
 * When a visitor asks the Website Agent to see the product, the server flips the
 * session into a live demo and a screencast track starts. The client used to
 * learn about that ONLY from the track: `demoActive = !!botVideoStream`, so the
 * stage mounted at the first frame. Measured on a prod call, the visitor then
 * watched ~7s of unexplained machinery — an empty dark stage, the app's parked
 * home page, a throttled navigation — before the agent said anything.
 *
 * The server now brackets the hand-over with two messages (`demo_starting`,
 * `demo_visible`) and the surface shows a branded card between them. The logic
 * lives here, as a pure reducer, for the reason `websiteAgentLayout.ts` exists:
 * WebsiteAgentView cannot be imported in the `node` test env, and "reveal on the
 * FIRST of three edges, but never before the picture exists" is exactly the kind
 * of rule that has to be pinned by a truth table rather than by a screenshot.
 *
 * Two invariants the phases encode:
 *
 *   - An OLD server sends neither message, so `startId` never leaves 0, the
 *     machine never leaves `idle`, and every caller behaves exactly as it did
 *     before — no card, stage mounted by the video track alone.
 *   - NO edge can reveal an empty box. `demo_visible` and the agent's first
 *     word are both routinely EARLIER than the first painted frame, so they arm
 *     the reveal and the frame commits it. The 12s cap is the same kind of edge:
 *     with a picture it reveals, without one it only arms. A browser that is
 *     still signing in (the first login of the day can take 30-60s) is a
 *     hand-over in progress, not a failed one — the card's line is still true,
 *     and unmounting the stage there made it vanish and pop back later, tearing
 *     down the presenter's canvas on the way. Only `give_up` releases a stage
 *     that never got a picture.
 */

/**
 * `idle`     — no hand-over in flight (also: an old server, forever).
 * `card`     — the branded card is over the picture; the stage is mounted.
 * `revealed` — the screencast owns the stage. Terminal until `demo_over`.
 */
export type DemoHandoverPhase = 'idle' | 'card' | 'revealed';

export interface DemoHandoverState {
  phase: DemoHandoverPhase;
  /**
   * The server's START counter this state belongs to, and the dedupe key WITHIN
   * one hand-over: a re-dispatch of the id already armed (a remount, a
   * double-invoked effect) must not wipe the END edge or the frame already seen.
   *
   * It does NOT survive `demo_over`, deliberately. The SDK retires its own
   * counters back to 0 on End, on a retry and on `demo_ended`, so the NEXT
   * hand-over in the same page session legitimately arrives as id 1 again —
   * a guard that outlived the reset would read that as a replay and the second
   * demo would get no card at all.
   */
  startId: number;
  /** An END edge has arrived and the reveal is waiting on the first frame. */
  endEdge: boolean;
  /** The `<video>` has actually started playing for this transition. */
  playing: boolean;
}

export const IDLE_DEMO_HANDOVER: DemoHandoverState = {
  phase: 'idle',
  startId: 0,
  endEdge: false,
  playing: false,
};

/**
 * How long the card waits for an END edge before it reveals on the picture
 * alone. Generous on purpose: the measured gap was ~7s, and a cap that fires
 * first would make the card look like the bug it exists to cover.
 */
export const DEMO_HANDOVER_CAP_MS = 12_000;

/**
 * How long a hand-over may go without ANY picture before the stage is released.
 * Matches the server's own ceiling for bringing the product up
 * (`DEMO_DISCOVERY_MAX_HOLD_S`, 90s): past that the demo is not coming, the
 * agent has moved on in conversation, and a card still promising it is a lie.
 */
export const DEMO_HANDOVER_GIVE_UP_MS = 90_000;

/** The cross-fade between the card and the picture. */
export const DEMO_HANDOVER_FADE_MS = 320;

export type DemoHandoverEvent =
  /** Server `demo_starting`. `startId` is the SDK's monotonic edge counter. */
  | { type: 'demo_starting'; startId: number }
  /** Server `demo_visible` — END edge (a). */
  | { type: 'demo_visible' }
  /** The agent started talking over the picture — END edge (b). */
  | { type: 'bot_speaking' }
  /** {@link DEMO_HANDOVER_CAP_MS} elapsed — END edge (c). */
  | { type: 'safety_cap' }
  /** {@link DEMO_HANDOVER_GIVE_UP_MS} elapsed with no picture at all. */
  | { type: 'give_up' }
  /** The screencast element reported playback. */
  | { type: 'video_playing' }
  /** The demo, its stream, or the session ended. */
  | { type: 'demo_over' };

/**
 * Advance the hand-over. Returns the SAME object for anything that changes
 * nothing, so a `useReducer` caller bails out of the re-render rather than
 * re-running the cap timer on every ignored event.
 */
export function reduceDemoHandover(
  state: DemoHandoverState,
  event: DemoHandoverEvent,
): DemoHandoverState {
  switch (event.type) {
    case 'demo_starting':
      // Monotonic within the hand-over: a replayed START for the id already
      // armed is not a second one. Across a `demo_over` the counter restarts
      // from 0 with the SDK's, so the next hand-over re-arms at id 1.
      if (!Number.isSafeInteger(event.startId) || event.startId <= state.startId) return state;
      return { phase: 'card', startId: event.startId, endEdge: false, playing: false };

    case 'demo_visible':
    case 'bot_speaking':
    case 'safety_cap':
      // An END edge outside a hand-over is not an error — the agent speaks all
      // day — it is simply not ours to act on. The cap is an END edge like the
      // other two: it never reveals a stage that has nothing to show.
      if (state.phase !== 'card') return state;
      if (state.playing) return { ...state, endEdge: true, phase: 'revealed' };
      return state.endEdge ? state : { ...state, endEdge: true };

    case 'video_playing':
      if (state.phase !== 'card') return state;
      if (state.endEdge) return { ...state, playing: true, phase: 'revealed' };
      return state.playing ? state : { ...state, playing: true };

    case 'give_up':
      // The only edge that ends a hand-over without a frame: see the file
      // header. A card with a picture behind it is the cap's business, not ours.
      if (state.phase !== 'card' || state.playing) return state;
      return { ...state, phase: 'revealed' };

    case 'demo_over':
      return state.phase === 'idle' ? state : IDLE_DEMO_HANDOVER;
  }
}

/** Is the branded card covering the stage? */
export function demoHandoverCardVisible(state: DemoHandoverState): boolean {
  return state.phase === 'card';
}

/**
 * Does the hand-over require the demo stage to be mounted, independently of the
 * video track?
 *
 * `card`: always. The stage has to exist before the track does — both to show
 * the card and to give the track something to attach to.
 *
 * `revealed`: only with a picture behind it. A hand-over that `give_up` ended
 * with nothing to show has to land on the PRE-CARD behaviour, and before the
 * card a demo that never streamed left no stage at all — so releasing it is the
 * honest fallback where parking an empty black box beside the chat is not. If
 * the screencast does turn up later, the track mounts the stage on its own, as
 * it always did.
 */
export function demoHandoverHoldsStage(state: DemoHandoverState): boolean {
  return state.phase === 'card' || (state.phase === 'revealed' && state.playing);
}
